import { TranslationKey } from './../../../../../types/translation/TranslationKey';

import React, { useContext, useState } from 'react';
import { t } from '../../../../../types/translation/Translator';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { CsvUploadConfiguration, CsvUploadTemplate, CsvUploadType } from '../../../../../types/csvUploadConfiguration';
import {
  CsvUploadConfigurationQueries,
  GetCsvUploadConfigurationForFileResponse,
  GetCsvUploadConfigurationForFileVariables,
} from '../../../../../graphql/csvUploadConfiguration.graphql';
import { useLazyQuery } from '@apollo/client';
import { Base64 } from 'js-base64';
import CreateCsvUploadConfigurationMapping from '../../../../components/CsvUploadConfiguration/CreateCsvUploadConfigurationMapping';
import { CsvUploadConfigurationSelect } from '../../../../components/CsvUploadConfiguration/CsvUploadConfigurationSelect';
import { Grid } from '@mui/material';
import UploadCsvInput from '../../../Common/UploadCsvInput';
import CsvUploadConfigurationInspect from '../../../../components/CsvUploadConfiguration/CsvUploadConfigurationInspect';
import Modal from '../../../../../VentoryUI/components/common/Modal/Modal';
import ModalPane from '../../../../../VentoryUI/components/common/Modal/components/ModalPane';
import { ButtonVariant, Button, NextButton, CancelButton } from '../../../../components/Button';

enum UserImportStatus {
  fileUpload,
  selectConfig,
  createConfig,
  inspect,
}

interface UserImportModalInputProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export default function UserImportModal({ open, setOpen }: UserImportModalInputProps) {
  const { currentCompany } = useContext(CompanyContext);

  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<UserImportStatus>(UserImportStatus.fileUpload);

  const [configurations, setConfigurations] = useState<CsvUploadConfiguration[]>([]);
  const [otherConfigurations, setOtherConfigurations] = useState<CsvUploadConfiguration[]>([]);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [requiredHeaders, setRequiredHeaders] = useState<string[]>([]);
  const [optionalHeaders, setOptionalHeaders] = useState<string[]>([]);
  const [fileAsBase64, setFileAsBase64] = useState<string | null>(null);
  const [deleteExisting, setDeleteExisting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [inspectId, setInspectId] = useState<string>();

  const [getConfigurations, { loading: getConfigurationsLoading }] = useLazyQuery<
    GetCsvUploadConfigurationForFileResponse,
    GetCsvUploadConfigurationForFileVariables
  >(CsvUploadConfigurationQueries.getForFile, {
    onCompleted: res => {
      setConfigurations(res.csvConfigurationsForFile.configurations.map(c => new CsvUploadConfiguration(c)));
      setFileHeaders(res.csvConfigurationsForFile.fileHeaders);
      setRequiredHeaders(res.csvConfigurationsForFile.requiredHeaders);
      setOptionalHeaders(res.csvConfigurationsForFile.optionalHeaders);
      setOtherConfigurations(res.csvConfigurationsForFile.otherConfigurations);
      if (
        !res.csvConfigurationsForFile.configurations.length &&
        !res.csvConfigurationsForFile.otherConfigurations.length
      ) {
        setStatus(UserImportStatus.createConfig);
      } else {
        setStatus(UserImportStatus.selectConfig);
      }
    },
    onError: err => setError(err.message),
  });

  const handleFile = (files: FileList | null) => {
    if (!files) {
      setFile(null);
      return;
    }
    setFile(files[0]);
  };

  const handleFileUpload = async () => {
    if (!file) return;

    let asBase64 = Base64.encode(await file.text());

    await getConfigurations({
      variables: {
        companyId: currentCompany.id,
        type: CsvUploadType.user,
        csvFileAsBase64Data: asBase64,
      },
    });
    setFileAsBase64(asBase64);
  };

  const handleClose = () => {
    setFile(null);
    setStatus(UserImportStatus.fileUpload);
    setConfigurations([]);
    setOtherConfigurations([]);
    setFileHeaders([]);
    setRequiredHeaders([]);
    setOptionalHeaders([]);
    setFileAsBase64(null);
    setOpen(false);
    setDeleteExisting(false);
    setError('');
  };

  const handleBack = () => {
    setError('');
    switch (status) {
      case UserImportStatus.fileUpload:
        return;
      case UserImportStatus.selectConfig:
        return setStatus(UserImportStatus.fileUpload);
      case UserImportStatus.createConfig:
        return setStatus(UserImportStatus.fileUpload);
      case UserImportStatus.inspect:
        return setStatus(UserImportStatus.selectConfig);
    }
  };

  const content = () => {
    if (status === UserImportStatus.createConfig && fileAsBase64) {
      return (
        <CreateCsvUploadConfigurationMapping
          handleUploadError={newConfiguration => {
            if (newConfiguration) setConfigurations([...configurations, newConfiguration]);
            setStatus(UserImportStatus.selectConfig);
          }}
          onClose={handleClose}
          onBack={handleBack}
          type={CsvUploadType.user}
          required={requiredHeaders}
          optional={optionalHeaders}
          headers={fileHeaders}
          csvFileAsBase64Data={fileAsBase64}
          setError={setError}
        />
      );
    }

    if (status === UserImportStatus.selectConfig && fileAsBase64) {
      return (
        <CsvUploadConfigurationSelect
          otherConfigurations={otherConfigurations}
          deleteExisting={deleteExisting}
          onClose={handleClose}
          onCreate={() => setStatus(UserImportStatus.createConfig)}
          onBack={handleBack}
          csvFileAsBase64Data={fileAsBase64}
          type={CsvUploadType.user}
          configurations={configurations}
          setError={setError}
          onInspect={id => {
            setInspectId(id);
            setStatus(UserImportStatus.inspect);
          }}
        />
      );
    }

    if (status === UserImportStatus.inspect) {
      return (
        <CsvUploadConfigurationInspect
          headers={fileHeaders}
          configuration={otherConfigurations.find(c => c.id === inspectId)}
          onBack={handleBack}
        />
      );
    }

    return (
      <ModalPane
        footer={
          <div className='flex justify-between gap-2'>
            <a
              style={{ textDecoration: 'none' }}
              href={`${process.env.REACT_APP_STORAGE_URL}/ventory/${CsvUploadTemplate.user}`}
              download={CsvUploadTemplate.user}
            >
              <Button
                variant={ButtonVariant.secondary}
                onClick={() => {}}
                text={t(TranslationKey.downloadTemplate).singular.label}
              />
            </a>
            <div className='flex gap-2'>
              <CancelButton disabled={getConfigurationsLoading} onClick={handleClose} />
              <NextButton disabled={!file} onClick={handleFileUpload} loading={getConfigurationsLoading} />
            </div>
          </div>
        }
      >
        <Grid container height={'100%'} alignContent={'space-between'}>
          <Grid item xs={12}>
            <Grid container rowSpacing={1}>
              <Grid item xs={12}>
                <UploadCsvInput file={file} onFile={handleFile} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ModalPane>
    );
  };

  return (
    <Modal
      open={open}
      error={error}
      onClose={handleClose}
      width='70%'
      height='650px'
      title={t(TranslationKey.importUsers).singular.label}
    >
      {content()}
    </Modal>
  );
}
